import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, FileText, Upload, Download, Clock, CheckCircle2, XCircle, AlertCircle, DollarSign } from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function ClaimDetails() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [claim, setClaim] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchClaim();
  }, [claimId]);

  const fetchClaim = async () => {
    try {
      const response = await axios.get(`${API}/claims/${claimId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaim(response.data);
    } catch (error) {
      toast.error("Failed to load claim details");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/claims/${claimId}/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Document uploaded successfully");
      fetchClaim();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
      case "paid":
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case "rejected":
        return <XCircle className="h-5 w-5 text-red-600" />;
      case "pending":
      case "under_review":
        return <Clock className="h-5 w-5 text-amber-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      under_review: "bg-purple-100 text-purple-800",
      paid: "bg-emerald-100 text-emerald-800",
      closed: "bg-gray-100 text-gray-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!claim) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
              Claim Details
            </h1>
            <p className="font-mono text-sm text-muted-foreground">{claim.claim_id}</p>
          </div>
          <Badge className={`${getStatusBadge(claim.status)} text-sm px-4 py-2`}>
            {claim.status.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Claim Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Policy ID</p>
                    <p className="font-mono text-sm">{claim.policy_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Filed On</p>
                    <p>{new Date(claim.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Job Loss Date</p>
                    <p>{new Date(claim.job_loss_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Last Employer</p>
                    <p>{claim.last_employer}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Reason for Job Loss</p>
                  <p className="text-sm bg-muted p-3 rounded-lg">{claim.reason_for_job_loss}</p>
                </div>
              </CardContent>
            </Card>

            {/* Documents */}
            <Card className="card-organic">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Documents</CardTitle>
                  {(claim.status === "pending" || claim.status === "under_review") && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-more-docs-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? "Uploading..." : "Upload More"}
                    </Button>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {claim.documents && claim.documents.length > 0 ? (
                  <div className="space-y-2">
                    {claim.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between bg-muted rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-primary" />
                          <div>
                            <p className="text-sm font-medium">{doc.filename}</p>
                            <p className="text-xs text-muted-foreground">
                              Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No documents uploaded yet
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Payments */}
            {claim.payments && claim.payments.length > 0 && (
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Payments Issued</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {claim.payments.map((payment, index) => (
                      <div key={index} className="flex items-center justify-between border-b border-border pb-3 last:border-0 last:pb-0">
                        <div className="flex items-center gap-3">
                          <div className="bg-green-100 p-2 rounded-full">
                            <DollarSign className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold">${payment.amount.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">
                              {payment.payment_type === "check" ? `Check #${payment.check_number}` : payment.payment_type}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-100 text-green-800">{payment.status}</Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(payment.issued_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Timeline */}
          <div>
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Status Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="timeline">
                  <div className={`timeline-item ${claim.status !== "pending" ? "completed" : "pending"}`}>
                    <div className="ml-2">
                      <p className="font-medium text-sm">Claim Filed</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(claim.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className={`timeline-item ${["under_review", "approved", "paid", "rejected"].includes(claim.status) ? "completed" : "pending"}`}>
                    <div className="ml-2">
                      <p className="font-medium text-sm">Under Review</p>
                      <p className="text-xs text-muted-foreground">
                        {claim.status === "under_review" ? "In Progress" : 
                         ["approved", "paid", "rejected"].includes(claim.status) ? "Completed" : "Pending"}
                      </p>
                    </div>
                  </div>

                  <div className={`timeline-item ${["approved", "paid"].includes(claim.status) ? "completed" : claim.status === "rejected" ? "rejected" : "pending"}`}>
                    <div className="ml-2">
                      <p className="font-medium text-sm">Decision</p>
                      <p className="text-xs text-muted-foreground">
                        {claim.status === "approved" ? "Approved" : 
                         claim.status === "rejected" ? "Rejected" :
                         claim.status === "paid" ? "Approved" : "Pending"}
                      </p>
                    </div>
                  </div>

                  {claim.status !== "rejected" && (
                    <div className={`timeline-item ${claim.status === "paid" ? "completed" : "pending"}`}>
                      <div className="ml-2">
                        <p className="font-medium text-sm">Payment Issued</p>
                        <p className="text-xs text-muted-foreground">
                          {claim.status === "paid" ? "Completed" : "Pending"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {claim.status === "pending" && (
                  <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      Your claim is being processed. We'll notify you when there's an update.
                    </p>
                  </div>
                )}

                {claim.status === "approved" && (
                  <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      Your claim has been approved! Payment will be issued shortly.
                    </p>
                  </div>
                )}

                {claim.status === "rejected" && (
                  <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">
                      Your claim was not approved. Contact support for more information.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
